import { Card } from '../../../molecules/Card/Card';
import { isEmpty, type ChartRow } from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type TopCommunitiesByMentionView = {
  /** `Most mentions` and `Most intense`. */
  kpis: KPIMetric[];
  /** One row per community, most mentions first. */
  rows: ChartRow[];
  xKey: string;
  series: ChartSeries[];
  /** Whether any community row is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
};

export type TopCommunitiesByMentionCardProps = WidgetBlockProps & {
  view?: TopCommunitiesByMentionView;
  /** Opens the conversations behind a community (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Figma `947:19575` — the groups generating the most conversation about us.
 *
 * Counts, ranked by them — so the community the first KPI names is the top bar
 * and its length is the reason. `Most intense` is a rate superlative, so it
 * takes a volume floor; the chart never does.
 */
export function TopCommunitiesByMentionCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: TopCommunitiesByMentionCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No community conversation in this period"
      {...cardState(status, hasData, onRetry)}
      title="Top communities by mention"
      helpText="The groups and forums generating the most conversation about us, and how intense that conversation is for their size."
      helpSupportingText="Only mentions posted inside a monitored community are counted. The second figure divides a community's mentions by its membership, so a small, loud group is not hidden behind a large, quiet one."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            onViewDetails={onViewDetails}
            series={view.series}
            orientation="horizontal"
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
